import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { EventListItem } from "../_components";
import { Button, Icon, Checkbox } from "../../_components";
import { useModal, useTime } from "../../_contexts";
import { EventActions } from "../../_actions";
import { useClickOutside, useEventListener } from "../../_hooks";
import { ChronosDate, getWorldEraPrefixes } from "../../_helpers";
import ReactTooltip from "react-tooltip";

export function EventListForm({ initialList, initialTerm }) {
  ReactTooltip.rebuild();
  const { items: events } = useSelector((state) => state.event);
  const { items: timelines } = useSelector((state) => state.timelines);
  const { details: world } = useSelector((state) => state.world);
  const [filteredEvents, setFilteredEvents] = useState(initialList || []);
  const [searchTerm, setSearchTerm] = useState(initialTerm || "");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [availableTimelines, setAvailableTimelines] = useState([]);

  const { openModal, closeModal } = useModal();

  const searchRef = useRef();
  const menuRef = useRef();
  const dispatch = useDispatch();

  const { currentDate, calendar, granularity, unitsDisplay, scrollPosition } =
    useTime();

  // console.log('Event Manager Calendar', calendar);
  const prefixes = getWorldEraPrefixes(world);
  const calculateDate = () => {
    const startDate = new ChronosDate(currentDate, {
      calendar,
    });
    const segment = startDate.getTimelineSegment(granularity, {
      unitCountDisplay: unitsDisplay,
    });

    const offset = scrollPosition;

    const dateAtLine = startDate.getDateFromPosition(
      offset,
      granularity,
      segment.start,
      unitsDisplay
    );
    return dateAtLine;
  };

  useClickOutside(menuRef, () => {
    if (availableTimelines && availableTimelines.length > 0) {
      setAvailableTimelines([]);
    }
  });

  const handleSearchChange = (ev) => {
    if (searchRef.current) {
      setSearchTerm(searchRef.current.value);
    }
  };

  const handleNewEvent = () => {
    console.log("Creating New Event!");
    const newEvent = {
      timeline: null,
      fromDate: calculateDate(),
      laneID: 0,
    };
    openModal("newEvent", {
      prevModal: "eventManager",
      eventItem: newEvent,
      // item: selectedItems
    });
  };

  const handleEventSelect = (eventID) => {
    const eventIndex = selectedEvents.findIndex((e) => e === eventID);
    if (eventIndex > -1) {
      setSelectedEvents((prevSelection) =>
        prevSelection.filter((i) => i !== eventID)
      );
    } else {
      setSelectedEvents((prevSelection) => [...prevSelection, eventID]);
    }
  };

  const handleAllEventSelect = () => {
    //If no events are selected:
    if (selectedEvents.length === 0) {
      //Get all filteredEvents and add to list
      // console.log('No prior selection');
      setSelectedEvents((prev) => [
        ...prev,
        ...filteredEvents.map((e) => e._id),
      ]);
      return;
    }

    //If all filteredEvents are visible:
    if (filteredEvents.every((e) => selectedEvents.includes(e._id))) {
      //Get filteredEvents, remove from list
      // console.log('Remove all visible');
      let eventsToRemove = filteredEvents.map((e) => e._id);
      setSelectedEvents((prev) =>
        prev.filter((e) => !eventsToRemove.includes(e))
      );
      return;
    }

    //If some visible events are selected:
    if (filteredEvents.some((e) => selectedEvents.includes(e._id))) {
      //Get all filteredEvents, add new ones to list
      // console.log('Some events selected');
      let newEvents = filteredEvents
        .filter((e) => !selectedEvents.includes(e._id))
        .map((e) => e.id);
      setSelectedEvents((prev) => [...prev, ...newEvents]);
      return;
    }

    return;
  };

  const handleFormClose = () => {
    closeModal();
  };

  const handleDeleteSelected = () => {
    openModal("confirmDelete", {
      prevModal: "eventManager",
      type: "eventMultiple",
      item: selectedEvents,
    });
  };

  const handleMoveSelected = (timelineID) => {
    //...Dispatch Update
    dispatch(EventActions.moveMultiple(timelineID, selectedEvents));
    setAvailableTimelines([]);
    setSelectedEvents([]);
  };

  const handleShowTimelines = (ev) => {
    setAvailableTimelines(timelines);
  };

  useEffect(() => {
    let newFilteredEvents = events.filter((ev) => {
      if (ev.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
      if (ev.description.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
      return false;
    });
    setFilteredEvents(newFilteredEvents);
  }, [searchTerm, events]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [selectedEvents]);

  const handleKeyClose = (ev) => {
    // console.log("Key up: ", ev);
    if (ev.key && ev.key.toLowerCase() === "escape") {
      // console.log("Form State: ", { ...formState });
      handleFormClose();
      // if (!formState.isValidating && !formState.isSubmitting) {
      //   if (
      //     formState.dirtyFields &&
      //     Object.keys(formState.dirtyFields).length === 0
      //   ) {
      //     console.log("dirty fields!");
      //     // handleFormClose();
      //   }
      // }
    }
  };

  useEventListener("keyup", handleKeyClose);
  // console.log('Selected Events', selectedEvents);
  const tooltipID = "chronosEventManagerTooltip";

  return (
    <div className="chronos-event-form chronos-event-manager chronos-list-form">
      <ReactTooltip id={tooltipID} effect="solid" />
      <div className="chronos-list-header">
        <div className="chronos-list-search">
          <input
            type="text"
            ref={searchRef}
            value={searchTerm}
            onChange={handleSearchChange}
            className="chronos-search-input chronos-input form-control"
            placeholder="Search Events"
          />
          <Icon icon="fas fa-search fa-fw" />
        </div>
        <div className="chronos-list-options">
          <Button color="success" onClick={handleNewEvent}>
            <Icon icon="fas fa-plus fa-fw" />
            &nbsp;New Event
          </Button>
        </div>
      </div>
      <div className="chronos-list-selector">
        <div className="chronos-list-select-all">
          <Checkbox
            custom
            // disabled={filteredEvents.length === 0}
            value={
              filteredEvents.every((e) => selectedEvents.includes(e._id)) &&
              selectedEvents.length > 0
            }
            onClick={handleAllEventSelect}
          />
          {/* {`(${selectedEvents.length}) Events Selected`} */}
        </div>
        <div className="chronos-list-select-options">
          {selectedEvents && selectedEvents.length > 0 && (
            <React.Fragment>
              <Button color="danger" onClick={handleDeleteSelected}>
                <Icon icon="far fa-trash fa-fw" />
                &nbsp;Delete {selectedEvents.length}
              </Button>
              <Button
                color="info"
                onClick={handleShowTimelines}
                style={{ marginLeft: "0.5rem" }}
                data-tip="Move selected Events to Timeline"
                data-for={tooltipID}
              >
                <Icon icon="far fa-arrow-from-left fa-fw" />
                &nbsp;Move To
              </Button>
              {availableTimelines && availableTimelines.length > 0 && (
                <ul className="chronos-event-list-dropdown" ref={menuRef}>
                  {availableTimelines.map((timeline) => (
                    <TimelineListOption
                      timeline={timeline}
                      onSelect={handleMoveSelected}
                      key={timeline._id}
                    />
                  ))}
                </ul>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
      <ul className="chronos-event-list">
        {filteredEvents.map((ev) => (
          <EventListItem
            event={ev}
            key={ev._id}
            onSelect={handleEventSelect}
            active={selectedEvents.includes(ev._id)}
            calendar={calendar}
            eraPrefixes={prefixes}
          />
        ))}
        {!filteredEvents ||
          (filteredEvents.length === 0 && (
            <div className="text-muted chronos-form-title chronos-no-result">
              No Events to Display
            </div>
          ))}
        <div className="form-controls">
          &nbsp;
          {/* <Button color="primary" onClick={handleFormClose}>
            <Icon icon="fas fa-times fa-fw" />
            &nbsp;Close
          </Button> */}
        </div>
      </ul>
    </div>
  );
}

function TimelineListOption({ timeline, onSelect }) {
  const handleTimelineSelect = () => {
    onSelect(timeline._id);
  };
  return (
    <div
      className="chronos-event-list-timeline-option"
      onClick={handleTimelineSelect}
    >
      <span className="chronos-event-list-timeline-option-title">
        {timeline.title}
      </span>
    </div>
  );
}
